import { useEffect, useMemo, type CSSProperties } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link2,
  Link2Off,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import "./rich-text-editor.css";

export interface RichTextEditorProps {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  /** CSS min-height for the editable area (default ~7rem) */
  minEditorHeight?: string;
  disabled?: boolean;
}

function MenuBar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null;

  const preventFocusSteal = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/40 p-1.5"
      onMouseDown={preventFocusSteal}
    >
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("heading", { level: 1 }) ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        aria-label="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("heading", { level: 2 }) ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        aria-label="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("heading", { level: 3 }) ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        aria-label="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>

      <span className="mx-0.5 h-6 w-px bg-border" aria-hidden />

      <Button
        type="button"
        size="icon"
        variant={editor.isActive("bold") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("italic") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("underline") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        aria-label="Underline"
      >
        <Underline className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("strike") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        aria-label="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("code") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleCode().run()}
        aria-label="Inline code"
      >
        <Code className="h-4 w-4" />
      </Button>

      <span className="mx-0.5 h-6 w-px bg-border" aria-hidden />

      <Button
        type="button"
        size="icon"
        variant={editor.isActive("bulletList") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        aria-label="Bullet list"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("orderedList") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        aria-label="Numbered list"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("blockquote") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        aria-label="Quote"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive("codeBlock") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        aria-label="Code block"
      >
        <span className="text-xs font-mono font-bold">{"{}"}</span>
      </Button>

      <span className="mx-0.5 h-6 w-px bg-border" aria-hidden />

      <Button
        type="button"
        size="icon"
        variant={editor.isActive({ textAlign: "left" }) ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        aria-label="Align left"
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive({ textAlign: "center" }) ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        aria-label="Align center"
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive({ textAlign: "right" }) ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        aria-label="Align right"
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={editor.isActive({ textAlign: "justify" }) ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        aria-label="Justify"
      >
        <AlignJustify className="h-4 w-4" />
      </Button>

      <span className="mx-0.5 h-6 w-px bg-border" aria-hidden />

      <Button
        type="button"
        size="icon"
        variant={editor.isActive("link") ? "secondary" : "ghost"}
        className="h-8 w-8"
        onClick={() => {
          const prev = editor.getAttributes("link").href as string | undefined;
          const url = window.prompt("Link URL", prev ?? "https://");
          if (url === null) return;
          if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
          }
          editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
        }}
        aria-label="Link"
      >
        <Link2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
        aria-label="Remove link"
      >
        <Link2Off className="h-4 w-4" />
      </Button>

      <span className="mx-0.5 h-6 w-px bg-border" aria-hidden />

      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        aria-label="Undo"
      >
        <Undo2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-8 w-8"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        aria-label="Redo"
      >
        <Redo2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * WYSIWYG editor (HTML). Compatible with React 19 (no `findDOMNode`).
 * Use {@link isHtmlContentEmpty} before sending optional fields to APIs.
 */
export function RichTextEditor({
  id,
  value,
  onChange,
  placeholder,
  className,
  minEditorHeight = "7rem",
  disabled,
}: RichTextEditorProps) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true, defaultProtocol: "https" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({
        placeholder: placeholder ?? "",
        emptyEditorClass: "is-editor-empty",
      }),
    ],
    [placeholder],
  );

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions,
      content: value,
      editable: !disabled,
      shouldRerenderOnTransaction: true,
      onUpdate: ({ editor: ed }) => {
        onChange(ed.getHTML());
      },
    },
    [extensions],
  );

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value === current) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  return (
    <div
      id={id}
      className={cn(
        "tiptap-rich-editor rounded-md border border-input bg-background overflow-hidden",
        disabled && "pointer-events-none opacity-60",
        className,
      )}
      style={
        {
          ["--rte-min-height" as string]: minEditorHeight,
        } as CSSProperties
      }
    >
      {!editor ? (
        <div
          className="min-h-[8rem] animate-pulse rounded-md bg-muted/30"
          aria-hidden
        />
      ) : (
        <>
          <MenuBar editor={editor} />
          <EditorContent editor={editor} className="tiptap-editor-content" />
        </>
      )}
    </div>
  );
}

/** True if HTML has no visible text (handles empty editor state e.g. `<p></p>`). */
export function isHtmlContentEmpty(html: string): boolean {
  if (!html || !html.trim()) return true;
  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.innerHTML = html;
    const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();
    return text.length === 0;
  }
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim()
    .length === 0;
}
